import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
    const navigate = useNavigate();

    // Scroll to top function
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Navigation handlers
    const handleExploreProducts = () => {
        navigate('/gallery');
        scrollToTop();
    };

    const handleContactUs = () => {
        navigate('/contact');
        scrollToTop();
    };

    // Array of premium background images for elegant slideshow
    const backgroundImages = [
        'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Modern bathroom with marble tiles
        'https://images.unsplash.com/photo-1620626011761-996317b8d101?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Tile showroom display
        'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Elegant bathroom design
        'https://images.unsplash.com/photo-1554995207-c18c203602cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Modern ceramic tiles
        'https://images.unsplash.com/photo-1604709177225-055f99402ea3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Luxury bathroom setup
        'https://images.unsplash.com/photo-1595428774223-ef52624120d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Beautiful marble bathroom
        'https://images.unsplash.com/photo-1625218122290-cb02adaf73db?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Granite countertop
        'https://images.unsplash.com/photo-1560185127-6ed189bf02f4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Modern bathroom fittings
        'https://images.unsplash.com/photo-1564540574859-0dfb63293365?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80', // Tile pattern showcase
        'https://images.unsplash.com/photo-1615873968403-89e068629265?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'  // Bathroom sink with tiles
    ];

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const handleImageTransition = useCallback(() => {
        setIsTransitioning(true);
        
        setTimeout(() => {
            setCurrentImageIndex((prevIndex) => 
                (prevIndex + 1) % backgroundImages.length
            );
            setIsTransitioning(false);
        }, 300); // Brief pause for elegant transition effect
    }, [backgroundImages.length]);

    useEffect(() => {
        const interval = setInterval(() => {
            handleImageTransition();
        }, 7000); // Slower, more elegant 7-second intervals

        return () => clearInterval(interval);
    }, [handleImageTransition]);

    const handleIndicatorClick = (index: number) => {
        if (index !== currentImageIndex) {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentImageIndex(index);
                setIsTransitioning(false);
            }, 300);
        }
    };

    return (
        <section className="hero">
            {/* Elegant background slideshow with crossfade effect */}
            <div className="hero-backgrounds">
                {backgroundImages.map((image, index) => (
                    <div
                        key={index}
                        className={`hero-background ${
                            index === currentImageIndex ? 'active' : ''
                        } ${isTransitioning ? 'transitioning' : ''}`}
                        style={{
                            backgroundImage: `linear-gradient(
                                rgba(0, 0, 0, 0.3), 
                                rgba(0, 0, 0, 0.5)
                            ), url(${image})`
                        }}
                    />
                ))}
            </div>
            
            {/* Elegant content overlay with subtle animations */}
            <div className="container">
                <div className="hero-content">
                    <h2 className="hero-title">Premium Tiles & Sanitary Solutions</h2>
                    <p className="hero-description">
                        Wholesale and retail supplier of high-quality tiles, granite, 
                        CP fittings, bath fittings, sanitary ware, and bath accessories
                    </p>
                    <div className="cta-buttons">
                        <button className="primary-btn" onClick={handleExploreProducts}>
                            Explore Products
                        </button>
                        <button className="secondary-btn" onClick={handleContactUs}>
                            Contact Us
                        </button>
                    </div>
                </div>
            </div>

            {/* Sophisticated indicator dots */}
            <div className="hero-indicators">
                {backgroundImages.map((_, index) => (
                    <button
                        key={index}
                        className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => handleIndicatorClick(index)}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>

            {/* Progress bar for current slide */}
            <div className="hero-progress">
                <div 
                    className="progress-bar"
                    style={{
                        animation: `slideProgress 7s linear infinite`
                    }}
                />
            </div>
        </section>
    );
};

export default Hero;